// apps/web/src/components/coletor/coletor-identidade.tsx — Fase 17.9, Plano 02 (KAN-06, KAN-07, D-02..D-08).
import { useEffect, useState, type ReactElement } from 'react';
import { obterSessaoAtual } from '../../lib/session';
import { apiMutate } from '../../lib/csrf-fetch';
import { EVENTO_SESSAO_EXPIRADA } from '../../lib/atividade-sessao';
import { obterCredencial, type CredencialDispositivo } from '../../lib/kanban/dispositivo';
import { TabletOperadorModal } from './tablet-operador-modal';
import { AjudaContextual } from '../ajuda/ajuda-contextual';

export interface ColetorBarraIdentidadeProps {
  operadorNome?: string | null;
  onSolicitarIdentificacao?: () => void;
  onSessaoEncerrada?: () => void;
}

export function ColetorBarraIdentidade({
  operadorNome,
  onSolicitarIdentificacao,
  onSessaoEncerrada,
}: ColetorBarraIdentidadeProps = {}): ReactElement {
  const [nome, setNome] = useState<string | null>(operadorNome ?? null);
  const [modalAberto, setModalAberto] = useState<boolean>(false);
  const [credencial, setCredencial] = useState<CredencialDispositivo | null>(null);

  useEffect(() => {
    if (operadorNome !== undefined) {
      setNome(operadorNome);
    }
  }, [operadorNome]);

  useEffect(() => {
    let ativo = true;
    void obterCredencial().then((cred) => {
      if (ativo) setCredencial(cred);
    });

    if (operadorNome === undefined) {
      void obterSessaoAtual().then((res) => {
        if (!ativo) return;
        if (res.autenticado) {
          setNome(res.usuario.displayName || res.usuario.username);
        } else {
          setNome(null);
        }
      });
    }

    const onExpirou = () => {
      setNome(null);
    };
    window.addEventListener(EVENTO_SESSAO_EXPIRADA, onExpirou);

    return () => {
      ativo = false;
      window.removeEventListener(EVENTO_SESSAO_EXPIRADA, onExpirou);
    };
  }, [operadorNome]);

  async function encerrarSessao(): Promise<void> {
    try {
      await apiMutate('/api/v1/auth/logout', { method: 'POST' });
    } catch {
      // Ignora falha de rede ao encerrar
    } finally {
      setNome(null);
      if (onSessaoEncerrada) {
        onSessaoEncerrada();
      }
    }
  }

  async function acaoIdentificarOuTrocar(): Promise<void> {
    if (onSolicitarIdentificacao) {
      onSolicitarIdentificacao();
      return;
    }

    const cred = credencial ?? (await obterCredencial().catch(() => null));
    if (cred) {
      setModalAberto(true);
      return;
    }

    // Sem credencial de dispositivo (coletor de mão): logout + tela de login
    try {
      await apiMutate('/api/v1/auth/logout', { method: 'POST' });
    } catch {
      // ignora
    } finally {
      window.location.assign('/login');
    }
  }

  const identificado = Boolean(nome);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-slate-100">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Operador neste tablet</p>
          <p className="truncate text-sm font-bold text-white">
            {identificado ? nome : 'Sem operador identificado (somente visualização)'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AjudaContextual pathname="/coletor" coletor />
          {identificado && (
            <button
              type="button"
              onClick={() => void encerrarSessao()}
              className="min-h-[48px] shrink-0 rounded-xl border border-slate-700 bg-slate-800 px-3 text-xs font-semibold text-slate-300 touch-manipulation hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Encerrar sessão
            </button>
          )}
          <button
            type="button"
            onClick={() => void acaoIdentificarOuTrocar()}
            className="min-h-[48px] shrink-0 rounded-xl border border-slate-600 bg-slate-800 px-4 text-sm font-bold text-slate-100 touch-manipulation hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            {identificado ? 'Trocar operador' : 'Identificar operador'}
          </button>
        </div>
      </div>

      {modalAberto && (
        <TabletOperadorModal
          aberto={modalAberto}
          credencial={credencial}
          onFechar={() => setModalAberto(false)}
          onAutenticado={(usuario) => {
            setNome(usuario.displayName || usuario.username);
            setModalAberto(false);
          }}
        />
      )}
    </>
  );
}
